import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import TabOutlinedIcon from '@mui/icons-material/TabOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import { ColorModeContext } from '../contexts/ColorModeContext';
import { useMenuLayout } from '../contexts/MenuLayoutContext';
import { LanguageContext } from '../contexts/LanguageContext';
import { getUser, clearAuth } from '../utils/auth';
import { queryClient } from '../lib/queryClient';
import { palette, typography, theme } from '../theme/colors';
import { NotificationBell } from './NotificationBell';
import { CurrencySelector } from './CurrencySelector';

const DRAWER_WIDTH = 220;

interface HeaderProps {
  onMenuClick: () => void;
}

export const Header = ({ onMenuClick }: HeaderProps) => {
  const { t } = useTranslation();
  const { mode, setMode } = useContext(ColorModeContext);
  const { language, setLanguage, supportedLanguages } = useContext(LanguageContext);
  const { layout, toggleLayout } = useMenuLayout();
  const user = getUser();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Dynamic drawer width based on layout mode
  const drawerWidth = layout === 'tabs' ? 170 : DRAWER_WIDTH;

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    surfaceHover: palette.surfaceHover,
    border: palette.border,
    borderLight: palette.borderLight,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    surfaceHover: palette.light.surfaceHover,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    clearAuth();
    queryClient.clear();
    navigate('/login');
  };

  const handleLanguageCycle = () => {
    const currentIndex = supportedLanguages.indexOf(language);
    const nextIndex = (currentIndex + 1) % supportedLanguages.length;
    setLanguage(supportedLanguages[nextIndex]);
  };

  const getLanguageLabel = (lang: string) => {
    return lang.toUpperCase();
  };

  const getRoleLabel = (role: string) => {
    const roleMap: Record<string, string> = {
      store_owner: 'OWNER',
      store_manager: 'MANAGER',
      store_staff: 'STAFF',
    };
    return roleMap[role] || role.toUpperCase();
  };

  const iconButtonStyles = {
    width: 34,
    height: 34,
    borderRadius: '8px',
    backgroundColor: 'transparent',
    color: colors.textSecondary,
    transition: theme.transitions.fast,
    '&:hover': {
      backgroundColor: alpha(colors.textSecondary, 0.08),
      color: colors.textPrimary,
    },
  };

  return (
    <Box
      component="header"
      sx={{
        position: 'fixed',
        top: 0,
        right: 0,
        left: { xs: 0, sm: drawerWidth },
        transition: `left ${theme.transitions.normal}`,
        height: 56,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2.5,
        backgroundColor: colors.bg,
        borderBottom: `1px solid ${colors.border}`,
        zIndex: 1100,
      }}
    >
      {/* Left Side */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton
          onClick={onMenuClick}
          sx={{
            display: { sm: 'none' },
            ...iconButtonStyles,
          }}
        >
          <MenuIcon sx={{ fontSize: 16 }} />
        </IconButton>

        {/* Search Button (Desktop) */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1,
            borderRadius: '8px',
            backgroundColor: colors.surface,
            border: `1px solid ${colors.border}`,
            cursor: 'pointer',
            transition: theme.transitions.fast,
            minWidth: 240,
            '&:hover': {
              borderColor: colors.borderLight,
              backgroundColor: colors.surfaceHover,
            },
          }}
        >
          <SearchOutlinedIcon sx={{ color: colors.textMuted, fontSize: 16 }} />
          <Typography
            variant="body2"
            sx={{
              color: colors.textMuted,
              fontSize: '13px',
              fontFamily: typography.fontFamily.body,
              flex: 1,
            }}
          >
            {t('common.search')}
          </Typography>
          <Box
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: '4px',
              backgroundColor: colors.surfaceHover,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: colors.textMuted,
                fontFamily: typography.fontFamily.mono,
                fontSize: '11px',
                fontWeight: typography.fontWeight.medium,
              }}
            >
              ⌘K
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Right Side */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {/* Theme Toggle */}
        <Tooltip title={mode === 'dark' ? 'LIGHT MODE' : 'DARK MODE'}>
          <IconButton onClick={() => setMode()} sx={iconButtonStyles}>
            {mode === 'dark' ? (
              <LightModeOutlinedIcon sx={{ fontSize: 15 }} />
            ) : (
              <DarkModeOutlinedIcon sx={{ fontSize: 15 }} />
            )}
          </IconButton>
        </Tooltip>

        {/* Layout Toggle */}
        <Tooltip title={layout === 'flat' ? 'SWITCH TO TABS LAYOUT' : 'SWITCH TO FLAT LAYOUT'}>
          <IconButton onClick={toggleLayout} sx={iconButtonStyles}>
            {layout === 'flat' ? (
              <TabOutlinedIcon sx={{ fontSize: 15 }} />
            ) : (
              <ViewListOutlinedIcon sx={{ fontSize: 15 }} />
            )}
          </IconButton>
        </Tooltip>

        {/* Currency Selector */}
        <CurrencySelector />

        {/* Notifications */}
        <NotificationBell />

        {/* Divider */}
        <Box
          sx={{
            width: 1,
            height: 24,
            backgroundColor: colors.border,
            mx: 1,
            display: { xs: 'none', sm: 'block' },
          }}
        />

        {/* User Menu */}
        {user && (
          <Box
            onClick={handleMenuOpen}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: 'pointer',
              py: 0.75,
              px: 1.5,
              borderRadius: '8px',
              transition: theme.transitions.fast,
              '&:hover': {
                backgroundColor: alpha(colors.textSecondary, 0.08),
              },
            }}
          >
            <Avatar
              sx={{
                width: 28,
                height: 28,
                backgroundColor: palette.primary,
                color: '#FFFFFF',
                fontFamily: typography.fontFamily.body,
                fontWeight: typography.fontWeight.semibold,
                fontSize: '12px',
              }}
            >
              {user.firstName?.[0]}{user.lastName?.[0]}
            </Avatar>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: typography.fontWeight.semibold,
                  color: colors.textPrimary,
                  fontSize: '13px',
                  lineHeight: 1.2,
                  fontFamily: typography.fontFamily.body,
                }}
              >
                {user.firstName} {user.lastName}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: colors.textMuted,
                  fontSize: '11px',
                  fontFamily: typography.fontFamily.body,
                }}
              >
                {getRoleLabel(user.roles?.[0]?.systemKey || user.roleSystemKeys?.[0] || '')}
              </Typography>
            </Box>
            <KeyboardArrowDownIcon
              sx={{
                color: colors.textMuted,
                fontSize: 18,
                display: { xs: 'none', md: 'block' },
              }}
            />
          </Box>
        )}

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 220,
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.surfaceElevated,
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
            },
          }}
        >
          {/* User Info Header */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${colors.border}`,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: typography.fontWeight.semibold,
                color: colors.textPrimary,
                fontFamily: typography.fontFamily.body,
                fontSize: '13px',
              }}
            >
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: colors.textMuted,
                display: 'block',
                fontSize: '12px',
                fontFamily: typography.fontFamily.body,
              }}
            >
              {user?.email}
            </Typography>
          </Box>

          <Box sx={{ py: 0.5 }}>
            <MenuItem
              onClick={handleMenuClose}
              sx={{
                py: 1,
                px: 2,
                borderRadius: '4px',
                mx: 0.5,
                '&:hover': {
                  backgroundColor: alpha(colors.textSecondary, 0.08),
                },
              }}
            >
              <ListItemIcon>
                <SettingsOutlinedIcon fontSize="small" sx={{ color: colors.textSecondary }} />
              </ListItemIcon>
              <Typography
                variant="body2"
                sx={{
                  color: colors.textPrimary,
                  fontFamily: typography.fontFamily.body,
                  fontWeight: typography.fontWeight.medium,
                  fontSize: '13px',
                }}
              >
                {t('navigation.items.settings')}
              </Typography>
            </MenuItem>

            <MenuItem
              onClick={handleLanguageCycle}
              sx={{
                py: 1,
                px: 2,
                borderRadius: '4px',
                mx: 0.5,
                '&:hover': {
                  backgroundColor: alpha(colors.textSecondary, 0.08),
                },
              }}
            >
              <ListItemIcon>
                <LanguageOutlinedIcon fontSize="small" sx={{ color: colors.textSecondary }} />
              </ListItemIcon>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.textPrimary,
                    fontFamily: typography.fontFamily.body,
                    fontWeight: typography.fontWeight.medium,
                    fontSize: '13px',
                  }}
                >
                  {t('header.language')}
                </Typography>
                <Box
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: '4px',
                    backgroundColor: colors.surfaceHover,
                    ml: 2,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.textMuted,
                      fontFamily: typography.fontFamily.mono,
                      fontSize: '11px',
                      fontWeight: typography.fontWeight.semibold,
                    }}
                  >
                    {getLanguageLabel(language)}
                  </Typography>
                </Box>
              </Box>
            </MenuItem>

            <MenuItem
              onClick={handleLogout}
              sx={{
                py: 1,
                px: 2,
                borderRadius: '4px',
                mx: 0.5,
                '&:hover': {
                  backgroundColor: alpha(palette.error, 0.08),
                },
              }}
            >
              <ListItemIcon>
                <LogoutOutlinedIcon fontSize="small" sx={{ color: palette.error }} />
              </ListItemIcon>
              <Typography
                variant="body2"
                sx={{
                  color: palette.error,
                  fontFamily: typography.fontFamily.body,
                  fontWeight: typography.fontWeight.medium,
                  fontSize: '13px',
                }}
              >
                {t('header.signOut')}
              </Typography>
            </MenuItem>
          </Box>
        </Menu>
      </Box>
    </Box>
  );
};
